import { useCallback, useEffect, useState } from "react";
import { banProbe, fetchProbes, liftProbeBan, vetoControl } from "../lib/api";
import { Ban, CheckCircle2, Undo2, XCircle } from "lucide-react";

const RESULT_STYLE = {
  pass: "text-zinc-100",
  fail: "text-zinc-400",
  partial: "text-zinc-300",
};

/** Standing operator control over the instrument bank: force, veto forward, veto backward. */
export const ProbeBank = ({ subject }) => {
  const [d, setD] = useState(null);
  const [ban, setBan] = useState({ type: "", turns: "", reason: "", categories: [] });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () => fetchProbes(subject).then(setD).catch(() => setD(null)),
    [subject]
  );

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, note) => {
    setBusy(true);
    setMsg("");
    try {
      const res = await fn();
      await load();
      setMsg(typeof note === "function" ? note(res) : note);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "that did not take");
    } finally {
      setBusy(false);
    }
  };

  const submitBan = () =>
    act(
      () =>
        banProbe({
          subject,
          type: ban.type,
          turns: ban.turns ? Number(ban.turns) : null,
          reason: ban.reason || null,
          categories: ban.categories.length ? ban.categories : null,
        }),
      (res) => {
        setBan({ type: "", turns: "", reason: "", categories: [] });
        return `${res.ban.type} withdrawn · ${res.note}`;
      }
    );

  const toggleVeto = (c) =>
    act(
      () => vetoControl({ subject, item_id: c.item_id, vetoed: !c.vetoed }),
      (res) =>
        `confidence ${res.confidence_before}% → ${res.confidence_after}% · ${res.n_control_items} items counting`
    );

  if (!d) {
    return <p className="font-data text-[11px] text-zinc-600">No instrument data for this subject.</p>;
  }

  return (
    <div data-testid="admin-probe-bank" className="space-y-12">
      <div className="grid grid-cols-2 gap-px bg-[var(--line)] sm:grid-cols-5">
        {[
          ["Counting", d.n_control_items, "scored, not vetoed"],
          ["Vetoed", d.n_control_vetoed, "struck from the math"],
          ["Types used", `${d.types_used.length}/7`, d.types_used.join(" · ") || "none yet"],
          ["Types needed", d.types_needed, "for the gate"],
          ["Withdrawn", d.types_banned.length, d.types_banned.join(" · ") || "nothing banned"],
        ].map(([k, v, s]) => (
          <div key={k} className="border border-[var(--line)] px-4 py-3">
            <p className="label">{k}</p>
            <p className="mt-1.5 font-data text-lg tabular-nums leading-none text-zinc-100">{v}</p>
            <p className="mt-1.5 truncate font-data text-[10px] text-zinc-600" title={s}>
              {s}
            </p>
          </div>
        ))}
      </div>

      {/* forward veto */}
      <div>
        <p className="label">Withdraw an instrument</p>
        <p className="mt-2 max-w-2xl text-xs leading-relaxed text-zinc-500">
          The Cartographer will not fire this type, nor a disguised version of it. The gate's type
          floor drops with it, so vetoing cannot lock the subject out of their own map.
        </p>
        <div className="mt-5 flex flex-wrap items-end gap-3">
          <select
            data-testid="probe-ban-type"
            value={ban.type}
            onChange={(e) => setBan({ ...ban, type: e.target.value })}
            className="border border-[var(--line-hi)] bg-[#0a0a0d] px-3 py-2 font-data text-[11px] text-zinc-100 outline-none"
          >
            <option value="">instrument type</option>
            {Object.keys(d.control_types).map((t) => (
              <option key={t} value={t} disabled={d.types_banned.includes(t)}>
                {t}
                {d.types_banned.includes(t) ? " (already withdrawn)" : ""}
              </option>
            ))}
          </select>
          <input
            data-testid="probe-ban-turns"
            type="number"
            min={1}
            value={ban.turns}
            onChange={(e) => setBan({ ...ban, turns: e.target.value })}
            placeholder="turns (blank = rest of session)"
            className="w-56 border border-[var(--line-hi)] bg-[#0a0a0d] px-3 py-2 font-data text-[11px] text-zinc-100 outline-none placeholder:text-zinc-700"
          />
          <input
            data-testid="probe-ban-reason"
            value={ban.reason}
            onChange={(e) => setBan({ ...ban, reason: e.target.value })}
            placeholder="why — recorded against your name"
            className="min-w-[16rem] flex-1 border border-[var(--line-hi)] bg-[#0a0a0d] px-3 py-2 font-data text-[11px] text-zinc-100 outline-none placeholder:text-zinc-700"
          />
          <button
            data-testid="probe-ban-submit"
            disabled={busy || !ban.type}
            onClick={submitBan}
            className="btn-stark flex items-center gap-2"
          >
            <Ban size={11} strokeWidth={1.8} /> Withdraw
          </button>
        </div>

        {d.control_types[ban.type] && (
          <p className="mt-4 max-w-2xl border-l border-[var(--line-hi)] pl-4 text-xs leading-relaxed text-zinc-500">
            {d.control_types[ban.type]}
          </p>
        )}

        <ul className="mt-7" data-testid="probe-ban-list">
          {(d.bans || []).map((b) => {
            const active = d.active_bans.some((a) => a.id === b.id);
            return (
              <li
                key={b.id}
                data-testid={`probe-ban-${b.type}`}
                className="flex flex-wrap items-baseline gap-3 border-b border-[var(--line)] py-3"
              >
                <span
                  className={`font-data text-[11px] uppercase tracking-[0.14em] ${
                    active ? "text-zinc-100" : "text-zinc-700 line-through"
                  }`}
                >
                  {b.type}
                </span>
                <span className="font-data text-[10px] text-zinc-600">
                  {b.until_turn === null
                    ? "rest of session"
                    : `until turn ${b.until_turn} (now ${d.subject_turns})`}
                  {b.categories?.length ? ` · ${b.categories.length} categories` : " · all topics"}
                  {b.reason ? ` · ${b.reason}` : ""} · {b.by}
                </span>
                <button
                  data-testid={`probe-ban-lift-${b.type}`}
                  disabled={busy}
                  onClick={() => act(() => liftProbeBan(b.id, subject), `${b.type} restored`)}
                  className="label ml-auto flex items-center gap-1.5 hover:text-zinc-100"
                >
                  <Undo2 size={11} strokeWidth={1.7} /> lift
                </button>
              </li>
            );
          })}
          {(d.bans || []).length === 0 && (
            <p className="font-data text-[11px] text-zinc-600">
              Every instrument is available to the Cartographer.
            </p>
          )}
        </ul>
      </div>

      {/* backward veto */}
      <div>
        <p className="label">Scored items · veto anything you disagree with</p>
        <p className="mt-2 max-w-2xl text-xs leading-relaxed text-zinc-500">
          A vetoed item stops counting toward predictive confidence and the gate immediately. Use it
          when the model has graded a hedge as a pass, or read honesty into a non-answer.
        </p>
        <ul className="mt-5" data-testid="probe-control-list">
          {(d.controls || []).map((c) => (
            <li
              key={c.item_id}
              data-testid={`control-item-${c.item_id}`}
              className={`grid grid-cols-[1fr_auto] items-start gap-4 border-b border-[var(--line)] py-3 ${
                c.vetoed ? "opacity-45" : ""
              }`}
            >
              <div className="min-w-0">
                <p className="font-data text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                  {c.item_id} · {c.type} ·{" "}
                  <span className={RESULT_STYLE[c.result] || "text-zinc-500"}>{c.result}</span>
                  {c.turn_score !== undefined ? ` · auth ${c.turn_score}` : ""}
                  {c.vetoed ? " · VETOED" : ""}
                </p>
                {c.note && (
                  <p className="mt-1.5 text-xs leading-relaxed text-zinc-400">{c.note}</p>
                )}
                {c.vetoed && (
                  <p className="mt-1.5 font-data text-[10px] text-zinc-600">
                    struck by {c.vetoed_by}
                    {c.veto_reason ? ` — ${c.veto_reason}` : ""}
                  </p>
                )}
              </div>
              <button
                data-testid={`control-veto-${c.item_id}`}
                disabled={busy}
                onClick={() => toggleVeto(c)}
                className="btn-stark flex items-center gap-2 !px-3 !py-2"
              >
                {c.vetoed ? (
                  <>
                    <CheckCircle2 size={11} strokeWidth={1.8} /> Reinstate
                  </>
                ) : (
                  <>
                    <XCircle size={11} strokeWidth={1.8} /> Veto
                  </>
                )}
              </button>
            </li>
          ))}
          {(d.controls || []).length === 0 && (
            <p className="font-data text-[11px] text-zinc-600">Nothing scored yet.</p>
          )}
        </ul>
      </div>

      {d.standing_orders?.length > 0 && (
        <div>
          <p className="label">Standing instrument orders</p>
          <ul className="mt-4" data-testid="probe-standing-orders">
            {d.standing_orders.map((o) => (
              <li key={o.id} className="border-b border-[var(--line)] py-3">
                <p className="font-data text-[11px] uppercase tracking-[0.14em] text-zinc-100">
                  {o.probe}
                  {o.category ? ` · ${o.category}` : ""}
                </p>
                <p className="mt-1.5 font-data text-[10px] text-zinc-600">
                  stands until fired · issued {o.issued_at?.slice(11, 19)} by {o.issued_by}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {msg && (
        <p data-testid="probe-note" className="font-data text-[11px] text-zinc-400">
          — {msg}
        </p>
      )}
    </div>
  );
};

export default ProbeBank;
